/**
 * Client SLA Repository
 * Handles all client SLA-related API calls
 */

import { apiClient } from '../client'
import type { PaginatedResponse } from './role.repository'
import type { Client } from './client.repository'

export interface ClientSlaKpi {
    id: number
    client_sla_id: number
    kpi_id: number
    frequency?: string | null
    track_by?: string | null
    default_load?: string | null
    kpi?: {
        id: number
        en_name: string
        ar_name?: string | null
    } | null
}

export interface ClientSla {
    id: number
    tenant_id: number
    client_id: number
    project_code?: string | null
    start_date?: Date | null
    end_date?: Date | null
    comments?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    client?: Client | null
    createdByUser?: {
        id: number
        en_name: string
    } | null
    updatedByUser?: {
        id: number
        en_name: string
    } | null
    kpis?: ClientSlaKpi[] | null
}

export interface CreateClientSlaRequest {
    tenant_id: number
    client_id: number
    project_code?: string
    start_date?: Date
    end_date?: Date
    comments?: string
    status?: number
    kpis?: Array<{
        kpi_id: number
        frequency?: string
        track_by?: string
        default_load?: string
    }>
}

export interface UpdateClientSlaRequest {
    tenant_id?: number
    client_id?: number
    project_code?: string
    start_date?: Date
    end_date?: Date
    comments?: string
    status?: number
    kpis?: Array<{
        kpi_id: number
        frequency?: string
        track_by?: string
        default_load?: string
    }>
}

class ClientSlaRepository {
    /**
     * Get all client SLAs with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<ClientSla>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.client_id) {
                params.append('client_id', filters.client_id)
            }
        }

        return apiClient.get<PaginatedResponse<ClientSla>>(`/client-sla?${params.toString()}`)
    }

    /**
     * Get client SLA by ID
     */
    async getById(id: number): Promise<ClientSla> {
        return apiClient.get<ClientSla>(`/client-sla/${id}`)
    }

    /**
     * Create a new client SLA
     */
    async create(data: CreateClientSlaRequest): Promise<ClientSla> {
        return apiClient.post<ClientSla>('/client-sla', data)
    }

    /**
     * Update an existing client SLA
     */
    async update(id: number, data: UpdateClientSlaRequest): Promise<ClientSla> {
        return apiClient.patch<ClientSla>(`/client-sla/${id}`, data)
    }

    /**
     * Delete a client SLA
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/client-sla/${id}`)
    }
}

// Export singleton instance
export const clientSlaRepository = new ClientSlaRepository()

